import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Dialog, Grid, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { CheckCircleRounded, CloseRounded, VisibilityOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandingSettingsApi } from '@/api/settings.api';
import { publicApi } from '@/api/public.api';
import Can from '@/components/common/Can';
import { useNotify } from '@/components/common/useNotify';
import { LOGIN_TEMPLATES, LoginTemplateView, type LoginTemplateId } from '@/components/auth/loginTemplates';
import type { LoginBrand } from '@/components/auth/useLoginForm';
import { useBranding } from '@/hooks/usePublicBranding';
import { qk } from '@/config/queryKeys';
import { storage } from '@/lib/storage';

/**
 * Settings › Branding › Sign-in page: the design shown on the company's OWN domain, with its name, colour
 * and logo. The TravelERP address always shows the TravelERP page, whatever is chosen here.
 */
export default function LoginPageSection({ current }: { current?: LoginTemplateId | null }) {
  const notify = useNotify();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<LoginTemplateId>(current ?? 'Skyline');
  const [preview, setPreview] = useState<LoginTemplateId | null>(null);
  useEffect(() => { if (current) setSelected(current); }, [current]);

  const brand = usePreviewBrand();
  const save = useMutation({
    mutationFn: (template: LoginTemplateId) => brandingSettingsApi.setLoginTemplate(template),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.branding }),
  });

  const doSave = async () => {
    try { await save.mutateAsync(selected); notify.success('Sign-in page saved.'); } catch (e) { notify.error(e); }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Typography variant="subtitle1" component="h2" fontWeight={700}>Sign-in page</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
          The design your team sees when they sign in on your own domain, with your company name, colour and logo.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          This applies on your own domain (for example erp.yourcompany.com) once TravelERP has connected it.
          Without your own domain, sign-in stays on the TravelERP page.
        </Alert>

        <Grid container spacing={1.5}>
          {LOGIN_TEMPLATES.map((t) => {
            const active = selected === t.id;
            return (
              <Grid key={t.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderWidth: 2, borderColor: active ? 'primary.main' : 'divider' }}>
                  <CardActionArea onClick={() => setSelected(t.id)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    <Box sx={{ height: 96, background: t.swatch, position: 'relative' }}>
                      {active && <CheckCircleRounded sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }} />}
                      {current === t.id && <Chip size="small" label="In use" sx={{ position: 'absolute', left: 8, top: 8, bgcolor: '#fff', fontWeight: 600 }} />}
                    </Box>
                    <Box sx={{ p: 1.5, flex: 1 }}>
                      <Typography fontWeight={700}>{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                    </Box>
                  </CardActionArea>
                  <Box sx={{ px: 1.5, pb: 1.25 }}>
                    <Button size="small" startIcon={<VisibilityOutlined />} onClick={() => setPreview(t.id)}>Preview</Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Can perm="Company.Edit">
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
            <Button variant="contained" onClick={doSave} disabled={save.isPending || selected === (current ?? 'Skyline')}>
              {save.isPending ? 'Saving…' : 'Use this design'}
            </Button>
          </Stack>
        </Can>
      </CardContent>

      <Dialog fullScreen open={preview !== null} onClose={() => setPreview(null)}>
        {preview && (
          <Box sx={{ position: 'relative' }}>
            <Stack direction="row" spacing={1} alignItems="center"
              sx={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10, bgcolor: 'rgba(15,23,42,0.85)', color: '#fff', borderRadius: 5, pl: 2, pr: 0.5, py: 0.5 }}>
              <Typography variant="body2">Preview · {preview} · signing in is off</Typography>
              <Tooltip title="Close preview">
                <IconButton size="small" onClick={() => setPreview(null)} sx={{ color: '#fff' }}><CloseRounded fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
            <LoginTemplateView template={preview} brand={brand} preview />
          </Box>
        )}
      </Dialog>
    </Card>
  );
}

/** The company's name, colour and logo as the sign-in page will show them. */
function usePreviewBrand(): LoginBrand {
  const { companyName, primaryColor, logoFileId } = useBranding();
  const blob = useQuery({
    queryKey: ['branding-logo-blob', storage.tenantCode],
    queryFn: publicApi.logoBlob,
    enabled: !!logoFileId,
    retry: false,
    staleTime: Infinity,
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) { setLogoUrl(null); return undefined; }
    const url = URL.createObjectURL(blob.data);
    setLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob.data]);

  return { companyName, primaryColor: primaryColor ?? '#1D4E89', logoUrl };
}
